/**
 * Business timezone enforcement for date display.
 *
 * The backend stores timestamps as Nepal (Asia/Kathmandu) wall-clock time, but
 * the ORM serializes those naive values as UTC `…Z` strings. So an order placed
 * at 9:00 AM Nepal arrives as `…T09:00:00Z`: the instant is labelled UTC, but the
 * clock digits ARE the Nepal wall-clock. We must therefore render the UTC clock
 * digits verbatim — i.e. format in UTC — to show the correct local time. Using
 * the viewer's browser timezone (or re-converting to Asia/Kathmandu) would add a
 * spurious +5:45 and show the time ~6 hours late.
 *
 * This module patches the three `Date` locale formatters to default to UTC when
 * the caller didn't specify a timezone, so display is correct and independent of
 * the viewer's machine clock.
 *
 * Scope / safety:
 *  - Only `Date.prototype` formatters are touched. `Number`/`BigInt`
 *    `toLocaleString` (currency, counts) are different methods and are NOT
 *    affected.
 *  - A caller can still override by passing an explicit `timeZone` option.
 *  - Display only: Date values, arithmetic, sorting and API payloads are
 *    unchanged.
 *
 * Imported once, first thing, in the app entry (src/index.tsx).
 */

export const APP_TIME_ZONE = "UTC";

const withAppTimeZone = (
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions => ({
  // Default to the business timezone; an explicit caller `timeZone` wins.
  timeZone: APP_TIME_ZONE,
  ...options,
});

const proto = Date.prototype as any;

const originalToLocaleString = proto.toLocaleString;
const originalToLocaleDateString = proto.toLocaleDateString;
const originalToLocaleTimeString = proto.toLocaleTimeString;

proto.toLocaleString = function (
  locales?: any,
  options?: Intl.DateTimeFormatOptions,
) {
  return originalToLocaleString.call(this, locales, withAppTimeZone(options));
};

proto.toLocaleDateString = function (
  locales?: any,
  options?: Intl.DateTimeFormatOptions,
) {
  return originalToLocaleDateString.call(this, locales, withAppTimeZone(options));
};

proto.toLocaleTimeString = function (
  locales?: any,
  options?: Intl.DateTimeFormatOptions,
) {
  return originalToLocaleTimeString.call(this, locales, withAppTimeZone(options));
};
